// src/features/datev/components/DatevSettings.tsx

import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Unlink, CheckCircle, AlertCircle, RefreshCw, AlertTriangle, ExternalLink, ShieldOff } from 'lucide-react';
import { datevEmployeeService, DatevApp, DatevConnectionStatus, DatevApiError } from '../../../services/datev/DatevEmployeeService';

interface DatevSettingsProps {
  customerId: string;
  beraternummer: string;
  mandantennummer: string;
  onUpdate?: () => void;
}

// Konfiguration der 4 DATEV-Apps. Werte müssen mit dem Backend-Mapping in
// netlify/functions/datev-auth.js (DATEV_APPS) und dem DatevApp-Typ übereinstimmen.
const DATEV_APP_LIST: Array<{ app: DatevApp; label: string; description: string }> = [
  {
    app: 'payrollreports',
    label: 'Lohnauswertungsdatenservice',
    description: 'Lesezugriff auf Lohnauswertungen (Brutto/Netto, Lohnsteuerbescheinigung, SV-Nachweis, u. v. m.).'
  },
  {
    app: 'exports',
    label: 'Lohnergebnisdatenservice',
    description: 'Strukturierte Lohnergebnisdaten (Stammdaten, Steuern, Sozialversicherung, Lohnzahlungen, Abwesenheiten).'
  },
  {
    app: 'exchange',
    label: 'Lohnaustauschdatenservice',
    description: 'Lesen und Schreiben von Lohndaten (Mitarbeiter anlegen, Beschäftigungszeiten, Monatsdaten).'
  },
  {
    app: 'eau',
    label: 'Elektronische Arbeitsunfähigkeitsbescheinigung',
    description: 'eAU-Anfragen erstellen und Rückmeldungen der Krankenkassen abrufen.'
  }
];

// Lokale Erweiterung des Service-Types: refreshTokenFirstIssuedAt kommt aus
// dem datev-auth-Backend (Status-Response) und stellt die Erstausstellung
// des aktuellen RT dar. Bleibt bei Refreshes konstant.
// TODO: Bei naechster Cleanup-Iteration in DatevConnectionStatus selbst aufnehmen.
type DatevConnectionStatusExt = DatevConnectionStatus & {
  refreshTokenFirstIssuedAt?: string | null;
};

type StatusMap = Partial<Record<DatevApp, DatevConnectionStatusExt>>;

// Strukturierter Error-State, damit DATEV-Hilfs-URL und -Error-Code im UI
// angezeigt werden koennen (DATEV-MUST: 4XXer Hilfs-URL anzeigen).
interface ErrorState {
  message: string;
  helpUrl: string | null;
  datevErrorCode: string | null;
}

// Audit 22.05.2026: Vier Berechtigungs-Zustaende pro App.
// 'connected'         = Token gueltig UND (Probe = granted ODER Probe nicht durchgefuehrt)
// 'permission_denied' = Stick-Login durchgefuehrt, DATEV verweigert Zugriff auf Berater-Mandant
// 'probe_error'       = Probe-Call nicht auswertbar (5XX / Netzwerk)
// 'not_connected'     = Noch keine Verbindung
type AppConnectionState = 'connected' | 'permission_denied' | 'probe_error' | 'not_connected';

function deriveAppState(details: DatevConnectionStatusExt | undefined): AppConnectionState {
  if (!details) return 'not_connected';
  if (details.permissionProbeStatus === 'denied') return 'permission_denied';
  if (details.permissionProbeStatus === 'error') return 'probe_error';
  if (details.connected === true) return 'connected';
  return 'not_connected';
}

export const DatevSettings: React.FC<DatevSettingsProps> = ({
  customerId,
  beraternummer,
  mandantennummer,
  onUpdate
}) => {
  const [statusByApp, setStatusByApp] = useState<StatusMap>({});
  const [error, setError] = useState<ErrorState | null>(null);
  const [refreshingApp, setRefreshingApp] = useState<DatevApp | null>(null);

  // Helper: nimmt einen Fehler aus dem Service und mappt ihn in unseren ErrorState.
  // Bei DatevApiError werden helpUrl und datevErrorCode mitgenommen, sonst nur die
  // Message. Damit ist die DATEV-MUST-Anforderung erfuellt: 4XXer-Hilfs-URLs werden
  // im UI angezeigt.
  const setErrorFromException = (err: unknown, fallback: string) => {
    if (err instanceof DatevApiError) {
      setError({ message: err.message, helpUrl: err.helpUrl, datevErrorCode: err.datevErrorCode });
    } else if (err instanceof Error) {
      setError({ message: err.message || fallback, helpUrl: null, datevErrorCode: null });
    } else {
      setError({ message: fallback, helpUrl: null, datevErrorCode: null });
    }
  };

  const loadAllStatuses = useCallback(async () => {
    try {
      // Alle 4 Apps parallel abfragen
      const entries = await Promise.all(
        DATEV_APP_LIST.map(async ({ app }) => {
          const status = await datevEmployeeService.getConnectionStatus(customerId, app);
          return [app, status] as const;
        })
      );
      const nextMap: StatusMap = {};
      entries.forEach(([app, status]) => {
        nextMap[app] = status;
      });
      setStatusByApp(nextMap);
    } catch (err) {
      console.error('Error loading statuses:', err);
    }
  }, [customerId]);

  // Initial Load
  useEffect(() => {
    loadAllStatuses();
  }, [loadAllStatuses]);

  const handleConnect = async (app: DatevApp) => {
    try {
      setError(null);
      // Pre-Check: Bei offline_access muss der Token an eine konkrete
      // Berater-Mandanten-Kombination gebunden werden. Beide Werte müssen
      // in den Stammdaten gepflegt sein, sonst lehnt das Backend den Authorize-
      // Aufruf ab. Wir prüfen hier UI-seitig, damit der User direkt eine
      // verständliche Fehlermeldung bekommt statt eines Backend-400-Roundtrips.
      if (!beraternummer || !mandantennummer) {
        setError({
          message: 'Bitte tragen Sie zuerst Beraternummer und Mandantennummer in den Stammdaten ein, bevor Sie eine DATEV-Verbindung aufbauen.',
          helpUrl: null,
          datevErrorCode: null
        });
        return;
      }
      // Hole Authorization URL vom Backend (app-spezifisch)
      const authUrl = await datevEmployeeService.getAuthorizationUrl(customerId, app);
      // Redirect zu DATEV OAuth
      window.location.href = authUrl;
    } catch (err) {
      setErrorFromException(err, 'Fehler beim Verbindungsaufbau zu DATEV');
      console.error('Connection error:', err);
    }
  };

  const handleDisconnect = async (app: DatevApp, label: string) => {
    if (!window.confirm(`Möchten Sie die DATEV-Verbindung für "${label}" wirklich trennen?`)) {
      return;
    }

    try {
      await datevEmployeeService.disconnect(customerId, app);
      await loadAllStatuses();
      if (onUpdate) onUpdate();
    } catch (err) {
      setErrorFromException(err, 'Fehler beim Trennen der Verbindung');
      console.error('Disconnect error:', err);
    }
  };

  const handleRefreshClients = async (app: DatevApp) => {
    setRefreshingApp(app);
    setError(null);
    try {
      await datevEmployeeService.refreshAuthorizedClients(customerId, app);
      // Status neu laden, damit der neue Cache im UI erscheint
      await loadAllStatuses();
    } catch (err) {
      setErrorFromException(err, 'Bestände konnten nicht aktualisiert werden');
    } finally {
      setRefreshingApp(null);
    }
  };

  // Token-Ablauf: formatiert das ISO-Datum aus dem Backend-Feld refreshTokenExpiresAt.
  // Backend berechnet seit 22.05.2026 pessimistisch ab refresh_token_first_issued_at
  // statt ab now(), damit das Datum bei Refreshes konstant bleibt (DATEV-Aussage:
  // "Token laeuft 2 Jahre ab Erstausstellung ab").
  const formatTokenExpiry = (isoString: string): string => {
    return new Intl.DateTimeFormat('de-DE', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(isoString));
  };

  // "vor X Minuten/Stunden/Tagen" für authorizedClientsFetchedAt-Anzeige
  const formatRelativeTime = (isoString: string): string => {
    const diffMs = Date.now() - new Date(isoString).getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    if (diffMinutes < 1) return 'gerade eben';
    if (diffMinutes < 60) return `vor ${diffMinutes} Minute${diffMinutes === 1 ? '' : 'n'}`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `vor ${diffHours} Stunde${diffHours === 1 ? '' : 'n'}`;
    const diffDays = Math.floor(diffHours / 24);
    return `vor ${diffDays} Tag${diffDays === 1 ? '' : 'en'}`;
  };

  return (
    <div>
      {/* Info Box */}
      <div style={{
        backgroundColor: '#EFF6FF',
        border: '1px solid #DBEAFE',
        borderRadius: '6px',
        padding: '12px',
        marginBottom: '20px',
        fontSize: '14px',
        color: '#1E40AF'
      }}>
        <strong>DATEV Integration:</strong> Authentifizieren Sie sich separat für jede Schnittstelle, die Sie nutzen möchten.
        Jede Schnittstelle wird in DATEV als eigenständige Anwendung geführt — Bestands- und Mandantenfreigaben werden
        pro Anwendung im DATEV-System vergeben.
        <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #DBEAFE' }}>
          <strong>Support:</strong> Bei Problemen mit der DATEV-Integration wenden Sie sich an{' '}
          <a href="mailto:support@xtax.de" style={{ color: '#1E40AF', textDecoration: 'underline' }}>
            support@xtax.de
          </a>.
        </div>
      </div>

      {/* Error Display - mit DATEV-Hilfs-URL (MUST: 4XXer Hilfs-URL anzeigen) */}
      {error && (
        <div style={{
          padding: '16px',
          backgroundColor: '#FEE2E2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          <p style={{ margin: 0, color: '#DC2626', fontSize: '14px', fontWeight: 500 }}>
            {error.message}
          </p>
          {error.datevErrorCode && (
            <p style={{ margin: '6px 0 0 0', color: '#991B1B', fontSize: '12px' }}>
              DATEV-Code: <code style={{ fontFamily: 'monospace' }}>{error.datevErrorCode}</code>
            </p>
          )}
          {error.helpUrl && (
            <p style={{ margin: '8px 0 0 0', fontSize: '13px' }}>
              <a
                href={error.helpUrl}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#1E40AF',
                  textDecoration: 'underline',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                DATEV-Hilfe zu diesem Fehler öffnen
                <ExternalLink size={12} />
              </a>
            </p>
          )}
        </div>
      )}

      {/* Vier App-Panels */}
      {DATEV_APP_LIST.map(({ app, label, description }) => {
        const details = statusByApp[app];
        const appState = deriveAppState(details);
        const isConnected = appState === 'connected';
        const isPermissionDenied = appState === 'permission_denied';
        const isProbeError = appState === 'probe_error';
        const isProblem = isPermissionDenied || isProbeError;

        // Border-Farbe: gruen=verbunden, rot=Berechtigungsproblem, grau=nicht verbunden
        const panelBorderColor = isConnected
          ? '#10B981'
          : isProblem
            ? '#DC2626'
            : '#E5E7EB';

        return (
          <div
            key={app}
            style={{
              padding: '20px',
              marginBottom: '16px',
              borderRadius: '8px',
              border: `1px solid ${panelBorderColor}`,
              backgroundColor: 'white'
            }}
          >
            {/* Header */}
            <div style={{ marginBottom: '12px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
                marginBottom: '4px'
              }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  DATEV {label}
                </h3>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  color: isConnected ? '#10B981' : (isProblem ? '#DC2626' : '#6B7280')
                }}>
                  {isConnected && (
                    <>
                      <CheckCircle size={16} />
                      Verbunden
                    </>
                  )}
                  {isPermissionDenied && (
                    <>
                      <ShieldOff size={16} />
                      Berechtigung verweigert
                    </>
                  )}
                  {isProbeError && (
                    <>
                      <ShieldOff size={16} />
                      Berechtigung nicht prüfbar
                    </>
                  )}
                  {!isConnected && !isProblem && (
                    <>
                      <AlertCircle size={16} />
                      Nicht verbunden
                    </>
                  )}
                </div>
              </div>
              <p style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>
                {description}
              </p>
            </div>

            {/* Berechtigungsproblem-Box (Audit 22.05.2026):
                Wird angezeigt, wenn der Permission-Probe-Call nach dem Stick-Login
                != HTTP 200 geliefert hat. Tokens wurden in dem Fall bereits bei
                DATEV widerrufen und nicht in unserer DB gespeichert. */}
            {isProblem && details && (
              <div style={{
                padding: '12px 14px',
                backgroundColor: '#FEF2F2',
                border: '1px solid #FECACA',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#7F1D1D',
                lineHeight: '1.6'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  marginBottom: '8px',
                  fontWeight: '600',
                  color: '#991B1B',
                  fontSize: '13px'
                }}>
                  <ShieldOff size={14} />
                  {isPermissionDenied
                    ? 'DATEV verweigert den Zugriff auf diese Berater-Mandant-Kombination.'
                    : 'Berechtigungsprüfung konnte nicht abgeschlossen werden.'}
                </div>

                {(details.beraternummer || details.mandantennummer) && (
                  <div>
                    <strong>Berater-Mandant:</strong>{' '}
                    {details.beraternummer || '?'}-{details.mandantennummer || '?'}
                  </div>
                )}

                {details.permissionProbeErrorCode && (
                  <div>
                    <strong>DATEV-Fehlercode:</strong>{' '}
                    <code style={{
                      fontFamily: 'monospace',
                      fontSize: '11px',
                      backgroundColor: '#FEE2E2',
                      padding: '1px 5px',
                      borderRadius: '3px'
                    }}>
                      {details.permissionProbeErrorCode}
                    </code>
                  </div>
                )}

                {details.permissionProbeHttpStatus !== null
                  && details.permissionProbeHttpStatus !== undefined && (
                  <div>
                    <strong>HTTP-Status:</strong> {details.permissionProbeHttpStatus}
                  </div>
                )}

                {details.permissionProbeErrorMessage && (
                  <div style={{ fontStyle: 'italic', marginTop: '4px' }}>
                    {details.permissionProbeErrorMessage}
                  </div>
                )}

                {details.tokenIssuedBy && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#991B1B' }}>
                    Stick-Login durch: <strong>{details.tokenIssuedBy}</strong>
                  </div>
                )}

                {details.permissionProbeAt && (
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#991B1B' }}>
                    Geprüft {formatRelativeTime(details.permissionProbeAt)}
                  </div>
                )}

                {details.permissionProbeHelpUrl && (
                  <div style={{ marginTop: '8px' }}>
                    <a
                      href={details.permissionProbeHelpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#1E40AF',
                        textDecoration: 'none',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '4px',
                        fontSize: '12px'
                      }}
                    >
                      DATEV-Hilfe öffnen <ExternalLink size={11} />
                    </a>
                  </div>
                )}

                <div style={{
                  marginTop: '8px',
                  paddingTop: '8px',
                  borderTop: '1px solid #FECACA',
                  fontSize: '11px',
                  color: '#7F1D1D'
                }}>
                  Die Tokens wurden bei DATEV widerrufen und sind nicht in unserer
                  Datenbank gespeichert. Bitte mit einem DATEV-Account erneut
                  authentifizieren, der für diese Berater-Mandant-Kombination
                  freigeschaltet ist.
                </div>
              </div>
            )}

            {/* Details bei Verbindung */}
            {isConnected && details && (
              <div style={{
                padding: '12px',
                backgroundColor: '#F9FAFB',
                borderRadius: '6px',
                marginBottom: '12px',
                fontSize: '12px',
                color: '#6B7280',
                lineHeight: '1.6'
              }}>
                {details.tokenIssuedBy && (
                  <div>Verbunden von: <strong>{details.tokenIssuedBy}</strong></div>
                )}
                {details.refreshTokenExpiresAt && (
                  <div>
                    Token gültig bis: <strong>{formatTokenExpiry(details.refreshTokenExpiresAt)}</strong>
                    {details.refreshTokenFirstIssuedAt && (
                      <span style={{ color: '#9CA3AF', marginLeft: '6px' }}>
                        (Erstausstellung {formatTokenExpiry(details.refreshTokenFirstIssuedAt)})
                      </span>
                    )}
                  </div>
                )}

                {/* Bestandsfreigaben (nur wenn vorhanden und > 0) */}
                {details.authorizedClients && details.authorizedClients.groups && details.authorizedClients.groups.length > 0 && (
                  <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #E5E7EB' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <strong style={{ color: '#374151' }}>
                        Freigegebene Bestände ({details.authorizedClients.groups.reduce((sum, g) => sum + g.count, 0)})
                      </strong>
                      <button
                        onClick={() => handleRefreshClients(app)}
                        disabled={refreshingApp === app}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '4px',
                          padding: '4px 8px',
                          backgroundColor: 'white', border: '1px solid #D1D5DB',
                          borderRadius: '4px',
                          cursor: refreshingApp === app ? 'not-allowed' : 'pointer',
                          fontSize: '11px', color: '#374151'
                        }}
                      >
                        <RefreshCw size={11} style={refreshingApp === app ? { animation: 'spin 1s linear infinite' } : undefined} />
                        Aktualisieren
                      </button>
                    </div>
                    <div style={{ paddingLeft: '4px' }}>
                      {details.authorizedClients.groups.map(g => (
                        <div key={g.consultant} style={{ marginBottom: '2px' }}>
                          Beraternr. <strong>{g.consultant}</strong>: Mandant {g.ranges.join(', ')}
                        </div>
                      ))}
                    </div>
                    {details.authorizedClientsFetchedAt && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#9CA3AF' }}>
                        Zuletzt aktualisiert {formatRelativeTime(details.authorizedClientsFetchedAt)}
                      </div>
                    )}
                  </div>
                )}

                {/* Warn-Hinweis: Token gültig, aber 0 Bestände (pragmatischer Status laut Tim) */}
                {details.authorizedClients && details.authorizedClients.groups && details.authorizedClients.groups.length === 0 && (
                  <div style={{
                    marginTop: '10px', padding: '8px 10px',
                    backgroundColor: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: '4px',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    fontSize: '12px', color: '#92400E'
                  }}>
                    <AlertTriangle size={14} />
                    <span>Keine Bestände freigegeben. Bitte prüfen Sie die Freigaben in DATEV.</span>
                    <button
                      onClick={() => handleRefreshClients(app)}
                      disabled={refreshingApp === app}
                      style={{
                        marginLeft: 'auto',
                        padding: '2px 8px',
                        backgroundColor: 'white', border: '1px solid #FCD34D',
                        borderRadius: '4px',
                        cursor: refreshingApp === app ? 'not-allowed' : 'pointer',
                        fontSize: '11px', color: '#92400E'
                      }}
                    >
                      Aktualisieren
                    </button>
                  </div>
                )}

                {details.datevTokenManagementUrl && (
                  <div style={{ marginTop: '4px' }}>
                    <a
                      href={details.datevTokenManagementUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#1E40AF', textDecoration: 'underline', fontSize: '11px' }}
                    >
                      DATEV Verbundene Anwendungen
                    </a>
                  </div>
                )}
              </div>
            )}

            {/* Button */}
            <div>
              {!isConnected ? (
                <button
                  onClick={() => handleConnect(app)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    backgroundColor: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <Link2 size={16} />
                  Authentifizieren
                </button>
              ) : (
                <button
                  onClick={() => handleDisconnect(app, label)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    padding: '8px 16px',
                    backgroundColor: '#DC2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  <Unlink size={16} />
                  Verbindung trennen
                </button>
              )}
            </div>
          </div>
        );
      })}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};
